import { PRODUCT } from "@/config/brand";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  BotMessageSquare,
  Radar,
  FlaskConical,
  Wrench,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";

interface Module {
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
  href?: string;
  cta?: string;
  status?: "live" | "roadmap";
}

const modules: Module[] = [
  {
    icon: ShieldCheck,
    eyebrow: "Spine pillar",
    title: PRODUCT.pillars.quality,
    description:
      "The quality pillar within Spine: documents, training, deviations, CAPA, change control, complaints, audits, suppliers, quality agreements, risk and validation — connected to QC Lab and Asset Integrity in the same record model.",
    bullets: [
      "Part 11-style e-signatures",
      "Append-only audit trail per record",
      "Access & segregation of duties enforced in the database",
    ],
    href: "/spine",
    cta: "Explore the eQMS",
    status: "live",
  },
  {
    icon: FlaskConical,
    eyebrow: "Spine pillar",
    title: PRODUCT.pillars.lab,
    description:
      "Sample lifecycle, test catalogue and specifications, results and review, out-of-specification handling, retains, reagents and standards, stability studies, environmental monitoring and certificates of analysis.",
    bullets: [
      "OOS handling linked to investigations",
      "Stability & environmental monitoring",
      "CoA / CoC from approved results",
    ],
    href: "/spine",
    cta: "See the lab scope",
    status: "live",
  },
  {
    icon: Wrench,
    eyebrow: "Spine pillar",
    title: PRODUCT.pillars.assets,
    description:
      "Assets, preventive maintenance plans, work orders with ISO 14224 failure coding, calibration with traceability to national standards, spare-part inventory and withdrawals — connected to the quality record.",
    bullets: [
      "Calibration & preventive maintenance",
      "Out-of-tolerance reaches affected lab results",
      "Work orders linked to deviations & changes",
    ],
    href: "/spine",
    cta: "See the maintenance scope",
    status: "live",
  },
  {
    icon: BotMessageSquare,
    eyebrow: "Native Spine capability",
    title: PRODUCT.capabilities.ai,
    description:
      "Governed, audit-trailed GxP assistants inside Spine’s compliance boundary for document drafting, deviation triage, CAPA assistance and audit readiness. Agents read only what your permissions allow, cite the records they used, and never write records or make GxP decisions.",
    bullets: [
      "Proposes and cites — never decides",
      "Every invocation logged",
      "Human review and approval remain required",
    ],
    href: "/ai-agents",
    cta: "Explore AI Agents",
    status: "live",
  },
  {
    icon: Radar,
    eyebrow: "Native Spine capability",
    title: PRODUCT.capabilities.regulatory,
    description:
      "Regulatory intelligence native inside Spine, connecting relevant guidance and enforcement trends to Change Control, Quality Risk Management (QRM) and the SOP lifecycle.",
    bullets: [
      "7+ authorities monitored",
      "Quarterly trending insights",
      "Change Control, QRM & SOP lifecycle",
    ],
    href: "/regulatory-hub",
    cta: "Explore Regulatory Watch",
    status: "live",
  },
];

const SuiteOverview = () => {
  return (
    <section className="py-20 md:py-28 bg-background" id="suite">
      <div className="container max-w-6xl">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-accent mb-4 px-3 py-1 rounded-full bg-accent/10">
            {PRODUCT.name}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
            What Pharmalify Spine covers
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Each module covers a defined set of workflows. Records link across modules with a full audit trail,
            and users sign in once from Pharmalify. Modules and workflows in scope depend on your licensed configuration.
          </p>
        </div>

        {[modules.slice(0, 3), modules.slice(3)].map((group, index) => (
          <div key={index} className="mb-10">
            <h3 className="text-lg font-semibold text-foreground mb-5">{index === 0 ? "The three pillars of Spine" : "Native capabilities within Spine"}</h3>
            <div className={`grid gap-6 ${index === 0 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
          {group.map((m) => {
            const isLink = m.status === "live" && m.href;
            const Wrapper: any = isLink ? Link : "div";
            const wrapperProps = isLink ? { to: m.href } : {};
            return (
              <Wrapper
                key={m.title}
                {...wrapperProps}
                className={`group relative flex flex-col rounded-2xl border border-border/60 bg-card p-7 transition-all duration-300 ${
                  isLink ? "hover:border-accent/50 hover:shadow-lg hover:-translate-y-1" : "opacity-90"
                }`}
              >
                {m.status === "roadmap" && (
                  <span className="absolute -top-2.5 right-6 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-bold tracking-widest uppercase border border-border">
                    On the roadmap
                  </span>
                )}
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                    <m.icon className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground">
                    {m.eyebrow}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3 tracking-tight">{m.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">{m.description}</p>
                <ul className="space-y-2 mb-6">
                  {m.bullets.map((b) => (
                    <li key={b} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-accent mt-0.5">›</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                {isLink ? (
                  <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-accent group-hover:gap-2.5 transition-all">
                    {m.cta}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                ) : (
                  <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
                    Coming next
                  </span>
                )}
              </Wrapper>
            );
          })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default SuiteOverview;
